import type { SLBPosition, LocalInventoryFilters, FilterDisplayOptions } from '@/types';
import { applyNumericCondition, isNumericConditionActive } from './apply-numeric-condition';

const FILTER_IDS_WHERE_ZERO_DISPLAYS_AS_HYPHEN = new Set([
  'pendingTransfersOut',
]);

function getCellValueForFilter(item: SLBPosition, filterId: string): number | string | null | undefined {
  const numericMap: Record<string, number | null | undefined> = {
    quantity: item.quantity,
    settledQuantity: item.settledQuantity,
    quantityT1: item.quantityT1,
    quantityT2: item.quantityT2,
    quantityTx: item.quantityTx,
    leverageRate: item.leverageRate,
    valueEUR: item.valueEUR,
    benchmarkFee: item.benchmarkFee,
    activeUtilisation: item.activeUtilisation,
    quantityOnLoan: item.quantityOnLoan,
    rebateRate: item.rebateRate,
    currencyBenchmark: item.currencyBenchmark != null ? parseFloat(String(item.currencyBenchmark)) : undefined,
    dailyPotential: item.dailyPotentialEUR,
    adtv30D: item.adtv30D,
    adtvMultiple: item.adtvMultiple,
    adtvAdjusted: item.adtvAdjustedPotentialEUR,
    l2cpQuantity: item.l2cpQuantity ?? undefined,
    l2cpAmountEur: item.l2cpAmountEur ?? undefined,
    l2cpRate: item.l2cpRate ?? undefined,
    l2cpGrossRate: item.l2cpGrossRate ?? undefined,
    l2cpSnpDelta: item.l2cpSnpDelta ?? undefined,
    pendingTransfersOut: item.pendingTransfers,
    cimbEq2: item.cimbEq2Quantity,
    cimbSf: item.cimbSfQuantity,
    cimbSf3: item.cimbSf3Quantity,
    velocityL: item.velocityLQuantity,
    velocityM: item.velocityMQuantity,
  };
  if (filterId in numericMap) return numericMap[filterId];
  const stringMap: Record<string, string | null | undefined> = {
    accountId: item.accountId,
    counterparty: item.counterparty,
    currency: item.currency,
    caStatus: item.caStatus,
    caType: item.caType,
    primaryAccountName: item.brokerAccountName,
    primarySettlementCp: item.brokerSettlementCounterparty,
    exDate: item.exDate,
  };
  if (filterId in stringMap) return stringMap[filterId];
  return undefined;
}

function isValueBlank(
  v: number | string | null | undefined,
  filterId: string
): boolean {
  if (v == null) return true;
  if (typeof v === 'number') {
    if (Number.isNaN(v)) return true;
    if (v === 0 && FILTER_IDS_WHERE_ZERO_DISPLAYS_AS_HYPHEN.has(filterId)) return true;
    return false;
  }
  const s = typeof v === 'string' ? v.trim() : '';
  return s === '' || s === '-';
}

export function applyInventoryLocalFilters(
  data: SLBPosition[],
  filters: LocalInventoryFilters,
  displayOptions: FilterDisplayOptions
): SLBPosition[] {
  let result = data;

  if (filters.accountIds.length > 0) {
    result = result.filter((item) =>
      filters.accountIds.includes(item.accountId)
    );
  }

  if (filters.counterparties.length > 0) {
    result = result.filter((item) =>
      filters.counterparties.includes(item.counterparty)
    );
  }

  if (filters.currencies.length > 0) {
    result = result.filter((item) =>
      filters.currencies.includes(item.currency)
    );
  }

  if (filters.exchanges.length > 0) {
    result = result.filter((item) =>
      filters.exchanges.includes(item.exchange)
    );
  }

  if (filters.caStatuses.length > 0) {
    result = result.filter((item) => {
      if (!item.caStatus) return false;
      const itemStatuses = item.caStatus.split(',').map(s => s.trim());
      return itemStatuses.some(status => filters.caStatuses.includes(status));
    });
  }
  if (filters.caTypes.length > 0) {
    result = result.filter((item) =>
      item.caType && filters.caTypes.includes(item.caType)
    );
  }
  if (filters.brokerAccountNames.length > 0) {
    result = result.filter((item) =>
      item.brokerAccountName && filters.brokerAccountNames.includes(item.brokerAccountName)
    );
  }
  if (filters.brokerSettlementCounterparties.length > 0) {
    result = result.filter((item) =>
      item.brokerSettlementCounterparty && filters.brokerSettlementCounterparties.includes(item.brokerSettlementCounterparty)
    );
  }

  if (isNumericConditionActive(filters.leverageRateCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.leverageRate, filters.leverageRateCondition)
    );
  }
  if (isNumericConditionActive(filters.valueEURCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.valueEUR, filters.valueEURCondition)
    );
  }
  if (isNumericConditionActive(filters.currencyBenchmarkCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(parseFloat(item.currencyBenchmark || '0'), filters.currencyBenchmarkCondition)
    );
  }
  if (isNumericConditionActive(filters.dailyPotentialCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.dailyPotentialEUR, filters.dailyPotentialCondition)
    );
  }
  if (isNumericConditionActive(filters.adtv30DCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.adtv30D, filters.adtv30DCondition)
    );
  }
  if (isNumericConditionActive(filters.adtvMultipleCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.adtvMultiple, filters.adtvMultipleCondition)
    );
  }
  if (isNumericConditionActive(filters.adtvAdjustedCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.adtvAdjustedPotentialEUR, filters.adtvAdjustedCondition)
    );
  }
  if (isNumericConditionActive(filters.benchmarkFeeCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.benchmarkFee, filters.benchmarkFeeCondition)
    );
  }
  if (isNumericConditionActive(filters.activeUtilisationCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.activeUtilisation, filters.activeUtilisationCondition)
    );
  }
  if (isNumericConditionActive(filters.quantityOnLoanCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.quantityOnLoan, filters.quantityOnLoanCondition)
    );
  }
  if (isNumericConditionActive(filters.rebateRateCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.rebateRate, filters.rebateRateCondition)
    );
  }
  if (isNumericConditionActive(filters.quantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.quantity, filters.quantityCondition)
    );
  }
  if (isNumericConditionActive(filters.settledQuantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.settledQuantity, filters.settledQuantityCondition)
    );
  }
  if (isNumericConditionActive(filters.quantityT1Condition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.quantityT1, filters.quantityT1Condition)
    );
  }
  if (isNumericConditionActive(filters.quantityT2Condition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.quantityT2, filters.quantityT2Condition)
    );
  }
  if (isNumericConditionActive(filters.quantityTxCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.quantityTx, filters.quantityTxCondition)
    );
  }
  if (isNumericConditionActive(filters.pendingTransfersOutCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.pendingTransfers, filters.pendingTransfersOutCondition)
    );
  }
  if (isNumericConditionActive(filters.cimbEq2QuantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.cimbEq2Quantity, filters.cimbEq2QuantityCondition)
    );
  }
  if (isNumericConditionActive(filters.cimbSfQuantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.cimbSfQuantity, filters.cimbSfQuantityCondition)
    );
  }
  if (isNumericConditionActive(filters.cimbSf3QuantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.cimbSf3Quantity, filters.cimbSf3QuantityCondition)
    );
  }
  if (isNumericConditionActive(filters.velocityLQuantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.velocityLQuantity, filters.velocityLQuantityCondition)
    );
  }
  if (isNumericConditionActive(filters.velocityMQuantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.velocityMQuantity, filters.velocityMQuantityCondition)
    );
  }
  if (filters.exDateFrom) {
    result = result.filter(
      (item) => item.exDate != null && item.exDate >= filters.exDateFrom!
    );
  }
  if (filters.exDateTo) {
    result = result.filter(
      (item) => item.exDate != null && item.exDate <= filters.exDateTo!
    );
  }

  if (filters.l2cpCounterparties?.length > 0) {
    result = result.filter((item) =>
      filters.l2cpCounterparties!.includes(item.counterparty)
    );
  }
  if (isNumericConditionActive(filters.l2cpQuantityCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.l2cpQuantity, filters.l2cpQuantityCondition)
    );
  }
  if (isNumericConditionActive(filters.l2cpAmountEurCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.l2cpAmountEur, filters.l2cpAmountEurCondition)
    );
  }
  if (isNumericConditionActive(filters.l2cpRateCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.l2cpRate, filters.l2cpRateCondition)
    );
  }
  if (isNumericConditionActive(filters.l2cpGrossRateCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.l2cpGrossRate, filters.l2cpGrossRateCondition)
    );
  }
  if (isNumericConditionActive(filters.l2cpSnpDeltaCondition)) {
    result = result.filter((item) =>
      applyNumericCondition(item.l2cpSnpDelta, filters.l2cpSnpDeltaCondition)
    );
  }

  // Apply display options (hideZero / hideBlanks)
  for (const [filterId, opts] of Object.entries(displayOptions)) {
    if (!opts) continue;
    if (opts.hideZero) {
      result = result.filter((item) => {
        const v = getCellValueForFilter(item, filterId);
        return typeof v !== 'number' || v !== 0;
      });
    }
    if (opts.hideBlanks) {
      result = result.filter((item) => {
        const v = getCellValueForFilter(item, filterId);
        return !isValueBlank(v, filterId);
      });
    }
  }

  return result;
}
